import { Form, Formik, FormikValues } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import SelectUser from "./components/SelectUser/SelectUser";
import SelectPlan from "./components/SelectPlan/SelectPlan";
import {
  fetchLessons,
  newMembership,
} from "../../services/ClassesService";
import { PlanI } from "../../interfaces/Plans";
import { AddPlanSchema } from "./schema/AddPlanSchema";
import { useUserContext } from "../../context/UserContext";
import { notifyError, notifySuccess } from "../../services/ToastNotifications";
import SelectPaymentMethod from "./components/SelectPaymentMethod/SelectPaymentMethod";
import EditPlansAmounts from "./components/EditPlansAmounts/EditPlansAmounts";
import SelectMembershipPlan from "./components/SelectMembershipPlan/SelectMembershipPlan";

const RESERVAS_SUELTAS_PLAN_ID = "230ca849-f427-41bd-b690-9935ab17f616";
const planOptions = [
  {
    id: RESERVAS_SUELTAS_PLAN_ID,
    name: "Reservas sueltas",
    description: "Plan para pagar reservas sueltas desde panel de maestros.",
  },
];

const AddPlan = () => {
  const [lessons, setLessons] = useState<PlanI[]>([]);
  const [isLoadingLessons, setIsLoadingLessons] = useState<boolean>(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string>(
    RESERVAS_SUELTAS_PLAN_ID
  );
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const { user } = useUserContext();
  const navigate = useNavigate();

  const initialValues = {
    selectedStudent: null,
    selectedPlanId: RESERVAS_SUELTAS_PLAN_ID,
    selectedPlans: [],
    paymentMethod: "cash",
    noReference: "",
  };

  const onSubmit = async (values: FormikValues) => {
    await Promise.all(
      values.selectedPlans.map(async (plan: PlanI) => {
        const planData = {
          userId: values.selectedStudent.value,
          planId: values.selectedPlanId,
          startDate: new Date(),
          freeClass: plan.lessons.pricing[plan.pricingIndex].reservations,
          duration: 1,
          typeDuration: "month",
          classId: plan.lessons.id,
          locationId: plan.locationId,
          payment: {
            amount: plan.lessons.pricing[plan.pricingIndex].cost,
            paymentMethod: values.paymentMethod,
            noReference: values.noReference,
            userCreatedId: user?.id,
          },
        };

        await newMembership(planData).then((res) => {
          if (res.error) {
            notifyError(
              "Ha ocurrido un error con la membresia de " + plan.name
            );
          } else {
            notifySuccess(
              "Se ha generado correctamente la membresia de " + plan.name
            );
          }
        });
      })
    );
    navigate("/pagos");
  };

  // funcion para obtener las lessons, filtrando las que no deben de aparecer
  // tambien agregandoles el campo priceIndex
  const getLessons = useCallback(async () => {
    if (!selectedPlanId || !selectedUserId) {
      setLessons([]);
      return;
    }

    setIsLoadingLessons(true);
    fetchLessons(selectedPlanId, selectedUserId)
      .then((res) => {
        if (!Array.isArray(res)) {
          throw new Error("No se pudieron cargar las clases para el plan.");
        }
        const temp = [...res];
        temp.map((plan) => {
          plan.lessons.pricing = plan.lessons.pricing.filter(
            (e: any) => e.showFront
          );
          plan.pricingIndex = 0;
        });

        setLessons(temp.filter((e) => e.name !== "SCULPT ROOM").reverse());
      })
      .catch((error) => {
        console.error(error);
        notifyError("No se pudieron cargar las clases del plan seleccionado.");
      })
      .finally(() => {
        setIsLoadingLessons(false);
      });
  }, [selectedPlanId, selectedUserId]);

  useEffect(() => {
    getLessons();
  }, [getLessons]);

  return (
    <div className="add-plan-main-container p-4 pt-8">
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={AddPlanSchema}
        validateOnMount
      >
        {(formikProps) => (
          <Form className="d-flex flex-column gap-4">
            <SelectMembershipPlan
              selectedPlanId={formikProps.values.selectedPlanId}
              plans={planOptions}
              onPlanSelect={(id) => {
                setSelectedPlanId(id);
                formikProps.setFieldValue("selectedPlanId", id);
                formikProps.setFieldValue("selectedPlans", []);
              }}
            />
            <SelectUser
              onUserChange={(id) => {
                setSelectedUserId(id);
                formikProps.setFieldValue("selectedPlans", []);
              }}
            />
            {formikProps.values.selectedStudent && (
              <SelectPlan
                lessons={lessons}
                isLoading={isLoadingLessons}
                userId={selectedUserId}
              />
            )}
            {formikProps.values.selectedPlans.length > 0 && (
              <EditPlansAmounts />
            )}
            {formikProps.values.selectedPlans.length > 0 && (
              <SelectPaymentMethod />
            )}
            {formikProps.values.selectedPlans.length > 0 && (
              <div className="shadow rounded p-4 bg-white">
                <label className="form-label" htmlFor="noReference">
                  Cuenta
                </label>
                <input
                  type="text"
                  id="noReference"
                  name="noReference"
                  className="form-control"
                  value={formikProps.values.noReference}
                  onChange={formikProps.handleChange}
                  onBlur={formikProps.handleBlur}
                  required
                />
                {formikProps.touched.noReference &&
                  formikProps.errors.noReference && (
                    <p className="text-danger mt-2 fs-14">
                      {formikProps.errors.noReference as string}
                    </p>
                  )}
              </div>
            )}
            {formikProps.isValid && formikProps.dirty && (
              <div className="row justify-content-center">
                <button
                  className="btn btn-primary px-4"
                  disabled={formikProps.isSubmitting}
                >
                  Agregar plan
                </button>
              </div>
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default AddPlan;
